import axios from "axios";
import { isLoggedIn } from "./isLoggedIn";

/**
 * Endpoints that DELIBERATELY attach `x-auth-token` but still return 200 with
 * data for anonymous (signed-out) users — they must never be blocked, or
 * anonymous browsing breaks. Keep this list in sync whenever a new public
 * token-optional endpoint is added. (Audited 2026-07; see the
 * `auth-on-mount-guard` note.)
 */
const PUBLIC_TOKEN_OPTIONAL = [
  /\/crowd-data\/locations(\?|$)/, // community map markers
  /\/search\/buy-rent(\?|$)/, // public buy/rent listings
  /\/saledeed\/preview(\?|$)/, // document preview
  /\/sro\/preview(\?|$)/, // document preview
  /\/[^/?]+\/view(\?|$)/, // /:id/view — public property view counter
];

/**
 * Did the caller attach an `x-auth-token` header? axios keeps the header key in
 * `config.headers` even when its value is `null` (the signed-out case), so
 * `.has()` reliably flags an intended auth request. Falls back to a key scan for
 * plain-object headers.
 */
function hasAuthTokenHeader(headers) {
  if (!headers) return false;
  if (typeof headers.has === "function") return headers.has("x-auth-token");
  return Object.keys(headers).some((k) => k.toLowerCase() === "x-auth-token");
}

/**
 * Attach a request interceptor that ABORTS any auth-required request (one that
 * carries an `x-auth-token` header) while there is NO session — such a call can
 * only ever 401 ("Please log in to continue"). Public token-optional endpoints
 * (above) are allow-listed so anonymous browsing keeps working. This is the one
 * central guarantee that "no token API is hit when signed out", independent of
 * every individual caller (present or future). Aborting lands in the same
 * catch / `.rejected` path a 401 would, so error handling is unchanged.
 *
 * @template T
 * @param {T} instance an axios instance
 * @returns {T} the same instance (for chaining)
 */
export function guardAuthRequests(instance) {
  instance.interceptors.request.use((config) => {
    if (hasAuthTokenHeader(config.headers) && !isLoggedIn()) {
      const url = config.url || "";
      const isPublic = PUBLIC_TOKEN_OPTIONAL.some((re) => re.test(url));
      if (!isPublic) {
        return Promise.reject(
          new axios.Cancel(`[auth-guard] blocked while signed out: ${url}`)
        );
      }
    }
    return config;
  });
  return instance;
}

export default guardAuthRequests;
